<template>
  <div class="flex flex-col flex-1 h-dvh">

    <!-- 
      zIndexes  

      z-0: Base elements
      z-10: navigation  bars, sidebars, buttons
      z-20: popups, dropdowns, modals, dialogs
      z-30: overlays, alerts, notifications. Our sidebar is z-30, considered an overlay
      z-40: critical elements (spinners, global toasts etc)
      (z-50 is the last of the z-indexes)
    -->

    <!-- Blurred overlay for mobile -->
    <!-- <div v-if="store.isSidebarOpen" class="fixed inset-0 bg-white/10 backdrop-blur-sm md:hidden z-20"
      @click="closeSidebar"></div> -->

    <!-- Sidebar for desktop -->
    <Sidebar v-if="hasSidebar && !isMobile()" :isMobile="isMobile()" />

    <!-- Main content wrapper with dynamic margin based on sidebar state -->
    <div :class="[
      'flex flex-1 h-full overflow-hidden',
      // Apply margins based on device type and sidebar state
      isMobile() ? 'flex-col mb-12' : (store.isSidebarOpen ? 'flex-col ml-72' : 'flex-col ml-16')
    ]">
      <TopBar v-if="!isMobile()" />
      <MobileTopBar v-if="isMobile() && isWatchlistPage()" />
      
      <!-- Main content area with scroll -->
      <main 
      :class="[
        'flex flex-col flex-1 overflow-hidden ',
        isMobile() ? '' : 'p-6'
      ]">
        <DialogStack />
        <LicenseKeyModal />
        <WelcomeModal />
        <SubscriptionLimitModal />
        <div v-if="shouldShowCenteredSearch" class="flex flex-col items-center justify-center h-2/3">
          <SearchBar :showTitle="true" :class="{'px-2 pt-10': isMobile()}" />
        </div>
       
        <!-- RouterView with fixed height and no overflow -->
        <div v-if="shouldShowEventsContainer" class="flex-1 h-full overflow-hidden">
          <RouterView class="h-full"/>
        </div>
      </main>

      <footer v-if="!isMobile()"
      :class="[
        'flex items-center text-gray-500 opacity-50 px-4',
        isMobile() ? 'justify-center' : 'justify-between py-3'
      ]">
        <div v-if="isMobile()" class="flex-1 flex justify-center">
          <div class="flex items-center gap-2">
            <span class="text-sm"><a href="https://status.polymarket.com" target="_blank">Polymarket Status</a></span>
            <div :class="[
              'w-2 h-2 rounded-full',
              polymarketStatus === 'UP' ? 'bg-green-500' : 'bg-orange-500'
            ]" />
          </div>
        </div>
        <div v-else v-tippy="{
          content: tooltipContent,
          allowHTML: true,
          interactive: true,
          theme: 'dark',
          placement: 'top-start'
        }">
          <div class="flex items-center gap-2">
            <span class="text-sm"><a href="https://status.polymarket.com" target="_blank">Polymarket Status</a></span>
            <div :class="[
              'w-2 h-2 rounded-full',
              polymarketStatus === 'UP' ? 'bg-green-500' : 'bg-orange-500'
            ]" />
          </div>
        </div>
        <div v-if="!isMobile()"><a href="/terms.html" target="_blank">Terms of Service</a></div>
        <div v-if="!isMobile()" class="invisible">Polymarket</div>
      </footer>
    </div>

    <!-- Sidebar for mobile - placed after main content -->
    <Sidebar v-if="hasSidebar && isMobile()" :isMobile="isMobile()" />

  </div>
</template>
<script>
import Sidebar from './components/Sidebar.vue';
import SearchBar from './components/SearchBar.vue';
import EventsMonitor from './components/EventTable.vue';
import SubscriptionsTable from './components/SubscriptionsTable.vue';
import Test from './Test.vue';
import { store, showDialog, isMobile } from './store';
import { confirmAuth } from './auth';
import { useRoute } from 'vue-router'
import TopBar from './components/TopBar.vue';
import MobileTopBar from './components/MobileTopBar.vue';
import EventTiles from './components/EventTiles.vue';
import DialogStack from './components/DialogStack.vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { computed, watch } from 'vue'
import TopSlugs from './components/TopSlugs.vue';
import crispService from './services/crisp';
import _ from 'lodash';
import ms from 'ms';
import { SubscriptionScope } from './types/Subscriptions';
import LicenseKeyModal from './components/LicenseKeyModal.vue'
import SubscriptionLimitModal from './components/SubscriptionLimitModal.vue'
import WelcomeModal from './components/WelcomeModal.vue'
import * as Sentry from '@sentry/vue';

export default {
  components: {
    Sidebar,
    SearchBar,
    EventsMonitor,
    TopBar,
    MobileTopBar,
    EventTiles,
    DialogStack,
    TopSlugs,
    SubscriptionsTable,
    Test,
    LicenseKeyModal,
    SubscriptionLimitModal,
    WelcomeModal,
  },
  data() {
    return {
      store,
      systemStatus: 'up',
      hasSidebar: true,
      count: 0,
      polymarketStatus: 'UP',
      activeIncidents: [],
      activeMaintenances: [],
    };
  },
  async created() {
    // Auth check and interval
    await confirmAuth(); 

    this.authInterval = setInterval(() => {
      confirmAuth();
    }, ms('5m'));
  },
  unmounted() {
    // Clean up event listeners
    clearInterval(this.authInterval);
    clearInterval(this.statusInterval);
  },
  async mounted() {
    if (!this.isMobile()) {
      await this.fetchPolymarketStatus();
      
      // Poll every 5 minutes
      this.statusInterval = setInterval(async () => {
        await this.fetchPolymarketStatus();
      }, 300000);
    }

    // Set up websocket
    this.websocketConnect();
  },
  computed: {
    statusIconClass() {
      switch (this.systemStatus) {
        case 'up':
          return 'text-green-500';
        case 'degraded':
          return 'text-yellow-500';
        case 'down':
          return 'text-red-500';
        default:
          return 'text-gray-500'; // Fallback color
      }
    },

    shouldShowEventsContainer() {
      return (
        _.size(this.route.params.listId) > 0 ||
        (_.size(this.route.params.marketName) > 0 && _.size(this.route.params.marketSlug) > 0)
      )
    },

    shouldShowCenteredSearch() {
      // Only show centered search bar on home page
      return this.route.path === '/';

      // Don't show centered search bar on market pages
      if (this.route.params?.marketName && this.route.params?.marketSlug) {
        return false;
      }

      if (this.route.path === '/subscriptions') {
        return false;
      }

      if (!this.route.params?.listId) {
        return true;
      }

      // Show centered search bar on Empty watchlists
      // const currentWatchlist = this.store?.watchLists?.find(
      //   item => item.id === this.route.params.listId
      // );

      // return currentWatchlist && 
      //        (!Object.keys(currentWatchlist.events || {}).length && 
      //         !Object.keys(currentWatchlist.markets || {}).length);
    },

    shouldShowSubscriptionsTable() {
      return this.route.path === '/subscriptions';
    },

    tooltipContent() {
      let content = '';

      if (this.activeIncidents.length > 0) {
        content += '<div class="font-semibold mb-1">Active Incidents:</div>';
        content += '<ul class="list-disc pl-4">';
        this.activeIncidents.forEach(incident => {
          content += `<li>${incident.name}</li>`;
        });
        content += '</ul>';
      }

      if (this.activeMaintenances.length > 0) {
        content += '<div class="font-semibold mb-1 mt-2">Scheduled Maintenance:</div>';
        content += '<ul class="list-disc pl-4">';
        this.activeMaintenances.forEach(maintenance => {
          content += `<li>${maintenance.name}</li>`;
        });
        content += '</ul>';
      }

      if (!this.activeIncidents.length && !this.activeMaintenances.length) {
        content = 'All systems operational';
      }

      return content;
    }
  },
  methods: {
    isMobile,
    isWatchlistPage() {
      return this.route.path.startsWith('/l/');
    },
      /* WebSocket */
      handleWebSocketMessage(event) {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'priceNotification') {
          const notification = data.payload;
          if (!notification.readFromUserAtMs) {
            notification.readFromUserAtMs = new Date().getTime();

            // Send acknowledgment back to server
            try {
              this.ws.send(JSON.stringify({
                type: 'readNotificationAck',
                payload: {
                  notificationId: notification.id,
                  readFromUserAtMs: notification.readFromUserAtMs
                }
              }));
            } catch (error) {
              Sentry.setExtra('method', 'handleWebSocketMessage::sendNotificationAck');
              Sentry.captureException(error);
              console.error('Error sending notification ack:', error);
            }
          }
          store.notifications.unshift(notification);
        } else if (data.type === 'eventStatusNotification') {
          const notification = data.payload;
          if (!notification.readFromUserAtMs) {
            notification.readFromUserAtMs = new Date().getTime();

            // Send acknowledgment back to server
            try {
              this.ws.send(JSON.stringify({
                type: 'readNotificationAck',
                payload: {
                  notificationId: notification.id,
                  readFromUserAtMs: notification.readFromUserAtMs
                }
              }));
            } catch (error) {
              Sentry.setExtra('method', 'handleWebSocketMessage::sendNotificationAck');
              Sentry.captureException(error);
              console.error('Error sending notification ack:', error);
            }
          }

          const statusText = notification.triggerType === 'Open' ? 'opened' : 'closed';
          const scopeText = notification.scope === 'Market' ? 'market' : 'event';

          store.notifications.unshift(notification);
        }
      } catch (error) {
        Sentry.setExtra('method', 'handleWebSocketMessage');
        Sentry.captureException(error);
        console.error('Error handling WebSocket message:', error);
      }
    },

    handleWebSocketClose() {
      setTimeout(() => {
        this.websocketConnect();
      }, 1000);
    },

    handleWebSocketOpen() {
      const token = localStorage.getItem('jwtToken');
      if (token) {
        this.ws.send(JSON.stringify({ type: 'authorization', payload: token }));
      }
    },

    websocketConnect() {
      if ([WebSocket.OPEN, WebSocket.CONNECTING].includes(this.ws?.readyState)) {
        return;
      }

      if (this.ws) {
        try {
          this.ws.close();
          this.ws.removeEventListener('message', this.handleWebSocketMessage);
          this.ws.removeEventListener('close', this.handleWebSocketClose);
          this.ws.removeEventListener('open', this.handleWebSocketOpen);
        } catch (error) {
          console.error('Error closing WebSocket:', error);
        }
      }

      this.ws = new WebSocket('wss://api.nevua.markets');
      this.ws.addEventListener('message', this.handleWebSocketMessage);
      this.ws.addEventListener('close', this.handleWebSocketClose);
      this.ws.addEventListener('open', this.handleWebSocketOpen);

    },

    reduceEvents(events) {
      return _.reduce(events, (acc, event, eventId) => {
        acc[eventId] = _.pick(event, ['markets']);
        return acc;
      }, {});
    },

    updateSystemStatus(status) {
      this.systemStatus = status;
    },

    loginWithGoogle() {
      const currentUrl = window.location.href;
      const url = new URL(currentUrl);
      const path = url.pathname;
      const oauthUrl = `https://api.nevua.markets/auth/oauth?state=${encodeURIComponent(path)}`;
      window.location.href = oauthUrl;
    },

    closeSidebar() {
      store.isSidebarOpen = false;
    },

    testDialog() {
      showDialog({
        message: `Test dialog ${this.count++}`,
        type: 'info',
        timeout: 60000
      });
    },

    async fetchPolymarketStatus() {
      try {
        const response = await fetch('https://api.nevua.markets/status/polymarket');
        const data = await response.json();
        this.polymarketStatus = data.page.status;
        this.activeIncidents = data.activeIncidents || [];
        this.activeMaintenances = data.activeMaintenances || [];
      } catch (error) {
        Sentry.setExtra('method', 'fetchPolymarketStatus');
        Sentry.captureException(error);
        console.error('Failed to fetch Polymarket status:', error);
      }
    }
  },
  setup() {
    const route = useRoute();
    const overscrollBehavior = computed(() =>
      store.isSidebarOpen && isMobile() ? 'none' : 'auto'
    )

    const isLoggedOut = computed(() => store.isLoggedOut)
    const isLoggedIn = computed(() => store.isLoggedIn)
    const failedToVerifyLoginLink = computed(() => store.failedToVerifyLoginLink)
    
    watch(isLoggedOut, (newValue, oldValue) => {
      if (newValue) {
        showDialog({
          message: 'You have been logged out',
          type: 'info'
        });
      }
      store.isLoggedOut = false;
    }, { immediate: true });

    watch(isLoggedIn, (newValue, oldValue) => {
      if (newValue) {
        showDialog({
          message: 'You have been logged in',
          type: 'info'
        });
      }
      store.isLoggedIn = false;
    }, { immediate: true });

    watch(failedToVerifyLoginLink, (newValue, oldValue) => {
      if (newValue) {
        showDialog({
          message: 'Failed to verify login link',
          type: 'error'
        });
      }
      store.failedToVerifyLoginLink = false;
    }, { immediate: true });

    watch(overscrollBehavior, (newValue) => {
      document.documentElement.style.setProperty('--overscroll-behavior', newValue)
    }, { immediate: true })

    watch(isMobile, (newValue) => {
      if (newValue) {
        console.log('On mobile, hiding chat');
        crispService.client.chat.hide()
        crispService.client.chat.onChatClosed(() => {
          crispService.client.chat.hide()
        })
      } else {
        console.log('On desktop, showing chat');
        crispService.client.chat.show()
        crispService.client.chat.onChatClosed(() => { })
      }
    })

    if (isMobile()) {
      console.log('On mobile, hiding chat');
      crispService.client.chat.hide()
      crispService.client.chat.onChatClosed(() => {
        crispService.client.chat.hide()
      })
    }

    return {
      route,
      isMobile,
      overscrollBehavior
    }
  }
};
</script>

<style>
#login-button {
  @apply bg-blue-500 text-white px-4 py-2 rounded;
}
</style>